/*
This is the root component of the application responsible for:
 - Application routing and navigation
 - Layout management
 - Authentication route protection
 - Sidebar state management

The component implements a responsive layout with:
 - Collapsible sidebar for navigation
 - Protected routes requiring authentication
 - Authentication flow routes
*/

import { useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sidebar } from "./components/layout";
import { Profile } from "./components/user";
import { useIsMobile } from "./hooks";
import { ChatSessionPage, NewChatPage } from "./components/chat";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  VerifyEmailPage,
  AcceptInvitePage,
  ProtectedRoute,
} from "./components/auth";
// Admin module - centralized imports
import {
  AdminProtectedRoute,
  DashboardPage,
  AgentsPage,
  AgentDetailPage,
  WorkspacesPage,
  WorkspaceMetricsPage,
  DatabasesPage,
  DatabaseDetailPage,
  DataSourcesPage,
  DataSourceDetailPage,
  SettingsPage,
  KnowledgeBaseLayout,
} from "./components/admin";
import { ProjectProvider } from "./contexts/ProjectContext";
import { AuthProvider } from "./contexts/AuthContext";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { ConversationsPage } from "./components/conversations";
import { ProjectsPage, ProjectDetailPage } from "./components/projects";
import { AgentCatalogPage } from "./components/agents";
import { getFeedback } from "./config/sentry";
import "./App.css";

// Utility function to get initial sidebar expanded state
const getInitialSidebarExpanded = () => {
  const storedState = localStorage.getItem("sidebarExpanded");
  // Default to collapsed (false)
  return storedState === "true";
};

// Component to handle feedback widget for non-authenticated users
const NonAuthFeedbackWidget = () => {
  const widgetRef = useRef<{ removeFromDom: () => void } | null>(null);

  useEffect(() => {
    const feedback = getFeedback();

    // Only create the widget if we're on a non-authenticated page
    // and if feedback is available
    if (feedback) {
      // Clean up existing widget before creating a new one
      if (widgetRef.current) {
        widgetRef.current.removeFromDom();
        widgetRef.current = null;
      }

      widgetRef.current = feedback.createWidget();

      return () => {
        // Clean up the widget when the component unmounts
        if (widgetRef.current) {
          widgetRef.current.removeFromDom();
          widgetRef.current = null;
        }
      };
    }
  }, []);

  return null; // This component doesn't render anything visually
};

/*
Main App Component
Manages the overall application layout and routing
*/
function App() {
  // State for sidebar expanded/collapsed
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(getInitialSidebarExpanded());
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // On mobile, sidebar starts closed
  // Reset sidebar state when switching between mobile/desktop
  useEffect(() => {
    if (isMobile) {
      setIsSidebarExpanded(false);
    }
  }, [isMobile]);

  // Toggle sidebar expanded state and store preference (desktop only)
  const toggleSidebar = () => {
    const newState = !isSidebarExpanded;
    setIsSidebarExpanded(newState);
    // Only persist preference on desktop
    if (!isMobile) {
      localStorage.setItem("sidebarExpanded", String(newState));
    }
  };

  /*
  Determine if current route is an authentication route
  Used to conditionally render header and sidebar
  */
  const isAuthRoute = [
    "/", // Login
    "/cadastro", // Registration
    "/esqueci-senha", // Forgot Password
    "/verify-email", // Email Verification
    "/accept-invite", // Accept Workspace Invite
  ].includes(location.pathname);

  return (
    <AuthProvider>
    <WorkspaceProvider>
    <ProjectProvider>
      <div className="tais-app">
        {/* For non-authenticated routes, show default feedback widget */}
        {isAuthRoute && <NonAuthFeedbackWidget />}

        {/* Mobile Header - Only visible on mobile for authenticated routes */}
        {!isAuthRoute && isMobile && (
          <header className="fixed top-0 left-0 right-0 h-14 bg-[#1a1a1a] border-b border-[#333333] z-40 flex items-center justify-between px-4">
            <button
              onClick={toggleSidebar}
              className="p-2 text-[#999999] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              aria-label="Abrir menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            <img
              src="/logo-tais.svg"
              alt="TAIS"
              className="w-8 h-8 cursor-pointer object-contain"
              onClick={() => navigate('/chat')}
            />
            <div className="w-10" /> {/* Spacer for centering logo */}
          </header>
        )}

        {/* Sidebar - always visible on desktop, drawer on mobile */}
        {!isAuthRoute && (
          <Sidebar
            isExpanded={isSidebarExpanded}
            onToggle={toggleSidebar}
            isMobile={isMobile}
          />
        )}

        {/* Main Layout */}
        <div
          className={`
            tais-app-layout
            ${!isAuthRoute
              ? isMobile
                ? "tais-app-layout--mobile"
                : isSidebarExpanded
                  ? "tais-app-layout--sidebar-expanded"
                  : "tais-app-layout--sidebar-collapsed"
              : ""
            }
          `}
        >
          {/* Main Content Area */}
          <div className="tais-app-content">
            <Routes>
              {/* Authentication Routes - New Design */}
              <Route
                path="/"
                element={
                  <ProtectedRoute authPage={true}>
                    <LoginPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cadastro"
                element={
                  <ProtectedRoute authPage={true}>
                    <RegisterPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/esqueci-senha"
                element={
                  <ProtectedRoute authPage={true}>
                    <ForgotPasswordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verify-email"
                element={
                  <ProtectedRoute authPage={true}>
                    <VerifyEmailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accept-invite"
                element={<AcceptInvitePage />}
              />

              {/* Protected Chat Routes */}

              {/* Protected Chat Routes - General Chats Only */}
              <Route path="/chat">
                {/* Chat Sub-routes */}
                <Route index element={<ProtectedRoute><NewChatPage /></ProtectedRoute>} /> {/* New Chat View */}
                <Route path=":sessionId" element={<ProtectedRoute><ChatSessionPage /></ProtectedRoute>} /> {/* Existing Chat Session */}
              </Route>

              {/* Protected Project Routes */}
              <Route path="/project/:projectId">
                {/* Project Sub-routes */}
                <Route index element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>} />
                <Route path="chat/:sessionId" element={<ProtectedRoute><ChatSessionPage /></ProtectedRoute>} />
              </Route>

              {/* Conversations Route - New dedicated page */}
              <Route
                path="/conversas"
                element={
                  <ProtectedRoute>
                    <ConversationsPage />
                  </ProtectedRoute>
                }
              />

              {/* Projects Route - New dedicated page */}
              <Route
                path="/projetos"
                element={
                  <ProtectedRoute>
                    <ProjectsPage />
                  </ProtectedRoute>
                }
              />

              {/* Agents Catalog Route */}
              <Route
                path="/agentes"
                element={
                  <ProtectedRoute>
                    <AgentCatalogPage />
                  </ProtectedRoute>
                }
              />

              {/* User Routes */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminProtectedRoute>
                    <DashboardPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/metrics"
                element={
                  <AdminProtectedRoute>
                    <WorkspaceMetricsPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/agents"
                element={
                  <AdminProtectedRoute>
                    <AgentsPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/agents/:id"
                element={
                  <AdminProtectedRoute>
                    <AgentDetailPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/workspaces"
                element={
                  <AdminProtectedRoute>
                    <WorkspacesPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/databases"
                element={
                  <AdminProtectedRoute>
                    <KnowledgeBaseLayout />
                  </AdminProtectedRoute>
                }
              >
                {/* Knowledge Base Sub-routes */}
                <Route index element={<DatabasesPage />} />
                <Route path=":id" element={<DatabaseDetailPage />} />
              </Route>
              <Route
                path="/admin/datasources"
                element={
                  <AdminProtectedRoute>
                    <DataSourcesPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/datasources/:id"
                element={
                  <AdminProtectedRoute>
                    <DataSourceDetailPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AdminProtectedRoute>
                    <SettingsPage />
                  </AdminProtectedRoute>
                }
              />
            </Routes>
          </div>
        </div>
      </div>
    </ProjectProvider>
    </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App;
